import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Star, X, Trash2, Camera, Loader2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

function MomentModal({ players, onClose, onSaved }) {
  const [f, setF] = useState({ player_id: players[0]?.id, date: today(), minuto: "", texto: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (f.texto.trim().length < 3) return toast.error("Describe el momento (ej. 'Golazo desde fuera del área')");
    setSaving(true);
    try {
      const { data: mom } = await api.post("/familias/momentos", {
        player_id: f.player_id, date: f.date,
        minuto: f.minuto !== "" ? Number(f.minuto) : null, texto: f.texto.trim(),
      });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/familias/momentos/${mom.id}/foto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Momento guardado ⭐");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center" data-testid="familia-moment-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border w-full max-w-md p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-black uppercase">Momento destacado</h3>
          <button onClick={onClose} data-testid="moment-close" className="w-8 h-8 border border-border hover:border-primary flex items-center justify-center"><X size={14} /></button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          {players.length > 1 ? (
            <select className="input-dark" value={f.player_id} onChange={(e) => setF({ ...f, player_id: e.target.value })} data-testid="moment-player">
              {players.map((p) => <option key={p.id} value={p.id}>{p.name.split(" ")[0]}</option>)}
            </select>
          ) : <div className="input-dark flex items-center text-muted-foreground truncate">{players[0]?.name.split(" ")[0]}</div>}
          <input type="date" className="input-dark" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="moment-date" />
          <input type="number" min="0" max="130" className="input-dark" placeholder="Minuto" value={f.minuto} onChange={(e) => setF({ ...f, minuto: e.target.value })} data-testid="moment-minuto" />
        </div>
        <textarea className="input-dark w-full h-20 py-2" placeholder="Ej. Gran pase que genera la ocasión del 2-1" maxLength={300} value={f.texto} onChange={(e) => setF({ ...f, texto: e.target.value })} data-testid="moment-texto" />
        <label className="flex items-center gap-2 h-11 px-3 border border-dashed border-border hover:border-primary cursor-pointer text-xs text-muted-foreground transition-colors" data-testid="moment-foto-label">
          <Camera size={14} className="text-primary" /> {file ? file.name : "Añadir foto (opcional)"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="moment-foto-input" />
        </label>
        <button onClick={save} disabled={saving} className="btn-volt w-full h-11" data-testid="moment-save-btn">
          {saving ? <Loader2 size={15} className="animate-spin mx-auto" /> : "Guardar momento"}
        </button>
      </div>
    </div>
  );
}

export const MomentsSection = ({ players }) => {
  const [moments, setMoments] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const all = await Promise.all(players.map((p) => api.get(`/familias/momentos?player_id=${p.id}`)));
      setMoments(all.flatMap((r) => r.data).sort((a, b) => (b.date || "").localeCompare(a.date || "")));
    } catch { /* silent */ }
  }, [players]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("¿Borrar este momento?")) return;
    try { await api.delete(`/familias/momentos/${id}`); load(); }
    catch { toast.error("No se pudo borrar"); }
  };

  return (
    <div className="space-y-3" data-testid="familia-moments-section">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-black uppercase text-sm text-muted-foreground">Álbum de momentos</h2>
        <button onClick={() => setShowModal(true)} data-testid="moments-new-btn" className="text-xs uppercase font-heading tracking-wider text-primary hover:underline inline-flex items-center gap-1">
          <Star size={12} /> + Momento
        </button>
      </div>
      {moments.length === 0 ? (
        <div className="border border-dashed border-border p-4 text-center text-xs text-muted-foreground" data-testid="moments-empty">
          Guarda las jugadas que no quieres olvidar: gol, gran parada, debut… con foto si quieres.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="moments-grid">
          {moments.slice(0, 6).map((m) => {
            const pl = players.find((p) => p.id === m.player_id);
            return (
              <div key={m.id} className="border border-border overflow-hidden" data-testid={`moment-card-${m.id}`}>
                {m.foto_url && <img src={`${BACKEND_URL}${m.foto_url}`} alt={m.texto} className="w-full h-24 object-cover" loading="lazy" />}
                <div className="p-2 space-y-1">
                  <div className="text-[10px] text-muted-foreground font-mono">{m.date}{m.minuto !== null && m.minuto !== undefined ? ` · min ${m.minuto}` : ""}{pl && players.length > 1 ? ` · ${pl.name.split(" ")[0]}` : ""}</div>
                  <div className="text-xs leading-snug">{m.texto}</div>
                  <button onClick={() => del(m.id)} data-testid={`moment-del-${m.id}`} className="text-[10px] text-muted-foreground hover:text-red-400 inline-flex items-center gap-1"><Trash2 size={10} /> Borrar</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showModal && <MomentModal players={players} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
    </div>
  );
};
